import { toPriceText } from './catalogCard'
import type { CatalogItemInput, PresentationContext } from './catalogCard'

export interface SuggestedLineModel {
  sku: string
  title: string
  imageUrl: string
  quantity: number
  /** `unit price × quantity`, already formatted. */
  amountText: string
}

export interface SuggestedOrderModel {
  lines: SuggestedLineModel[]
  /** The raw total, for callers that need to compare rather than display. */
  total: number
  totalText: string
  /** Every sku in the basket, so "add all" is one call and not a loop of models. */
  skus: string[]
  /** Sum of the quantities, which is what "12 productos" counts. */
  units: number
}

/** A line as a recommendation field returns it: the sku, and how many. */
export interface RecommendedLineInput {
  sku?: string | null
  quantity?: number | null
}

/**
 * The quantities a recommendation answered with, as `toSuggestedOrderModel`
 * takes them.
 *
 * A server that recommends a product without saying how many means one — the
 * field is optional on `SuggestedItemReturned`, and dropping those lines would
 * silently shrink a basket the storefront proposed. A quantity of zero or less
 * is different: that is the server saying "not this one", so it stays out.
 *
 * The first quantity for a sku wins. A recommendation list repeating a sku is
 * the server contradicting itself, and summing would turn "2 or 3 of these"
 * into five.
 */
export const toSuggestedQuantities = (
  lines: readonly RecommendedLineInput[] | null | undefined,
): Record<string, number> => {
  const quantities: Record<string, number> = {}

  for (const line of lines ?? []) {
    const sku = line?.sku
    if (!sku || sku in quantities) continue

    const quantity = line.quantity ?? 1
    if (quantity <= 0) continue

    quantities[sku] = quantity
  }

  return quantities
}

/**
 * A pre-filled basket the storefront proposes: "your usual order, ready to add".
 *
 * The quantities come from the host: today that is a recommendation field
 * (`suggestedItems`, `buyAgain`, `prefillCart`), which answers with a quantity
 * per sku — see `toSuggestedQuantities`. What *is* domain here is turning a sku
 * and a quantity into a line with a formatted amount, and doing it exactly the
 * way the catalog card does: same placeholder fallback, same `toPriceText`, so
 * a hidden price is hidden in both places.
 *
 * A sku with no matching product is skipped rather than rendered blank — a
 * suggestion for something the catalog no longer sells is not a line, it is a
 * stale suggestion.
 */
export const toSuggestedOrderModel = (
  items: readonly CatalogItemInput[] | null | undefined,
  quantitiesBySku: Readonly<Record<string, number>> | null | undefined,
  ctx: PresentationContext,
): SuggestedOrderModel => {
  const bySku = new Map((items ?? []).map((item) => [item.sku, item]))
  const lines: SuggestedLineModel[] = []
  let total = 0
  let units = 0

  for (const [sku, quantity] of Object.entries(quantitiesBySku ?? {})) {
    const item = bySku.get(sku)
    if (!item || quantity <= 0) continue

    const amount = item.price * quantity
    total += amount
    units += quantity

    lines.push({
      sku,
      title: item.name,
      imageUrl: item.image || ctx.placeholderImage,
      quantity,
      amountText: toPriceText(amount, ctx),
    })
  }

  return {
    lines,
    total,
    totalText: toPriceText(total, ctx),
    skus: lines.map((line) => line.sku),
    units,
  }
}
